import { api } from "../../utils/api";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";

const RedemptionHistory = ({ vehicles, customerId }) => {
  const navigate = useNavigate();
  const [redemptionHistory, setRedemptionHistory] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [removing, setRemoving] = useState(false);
  const isAdmin = sessionStorage.getItem("selectedPortal") === "Administrator";

  // Function to normalize the vehicles input
  const normalizeVehicles = (vehicles) => {
    if (!vehicles) return [];
    if (Array.isArray(vehicles)) {
      return vehicles
        .filter((vehicle) => vehicle !== null && vehicle !== undefined)
        .map((vehicle) => (typeof vehicle === "object" ? vehicle._id : vehicle));
    }
    return [typeof vehicles === "object" ? vehicles._id : vehicles];
  };

  // Admin: query redemptions directly by vehicle IDs (source of truth). Subscription-based
  // fetch fails when subscription/customer excludes inactive vehicles.
  const fetchByVehiclesForAdmin = async (vehicleIds) => {
    const response = await api("v1/redemption/search", "POST", {
      query: { vehicle: { $in: vehicleIds } },
      selectedPortal: "Administrator"
    });
    return response.data.success && response.data.data?.searchResult
      ? response.data.data.searchResult
      : [];
  };

  const fetchByCustomerSubscriptions = async (customerId) => {
    const subsResponse = await api(`v1/subscription/customer/${customerId}`, "GET");
    if (!subsResponse.data.success || !subsResponse.data.data?.allSubscriptions) {
      return [];
    }
    const subscriptions = subsResponse.data.data.allSubscriptions;
    const redemptionIds = [];
    subscriptions.forEach((sub) => {
      const redemptions = sub.redemptions || [];
      redemptions.forEach((r) => {
        const id = typeof r === "object" && r !== null ? r._id : r;
        if (id) redemptionIds.push(id);
      });
    });
    if (redemptionIds.length === 0) return [];
    const response = await api(`v1/redemption/search`, "POST", {
      query: { _id: { $in: redemptionIds } },
      selectedPortal: "Administrator"
    });
    const list = response.data.success && response.data.data?.searchResult
      ? response.data.data.searchResult
      : [];
    const customerVehicleIds = new Set(normalizeVehicles(vehicles).map((id) => String(id)));
    if (customerVehicleIds.size === 0) return list;
    return list.filter((r) => {
      const vid = r.vehicle?._id || r.vehicle;
      return vid && customerVehicleIds.has(String(vid));
    });
  };

  const fetchByVehicles = async (vehicleIds) => {
    const selectedPortal = sessionStorage.getItem("selectedPortal");
    const response = await api(`v1/redemption/search`, "POST", {
      query: {
        vehicle: { $in: vehicleIds },
        active: true
      },
      selectedPortal: selectedPortal
    });
    return response.data.success && response.data.data?.searchResult
      ? response.data.data.searchResult
      : [];
  };

  useEffect(() => {
    const fetchRedemptionHistory = async () => {
      setFetchLoading(true);
      if (isAdmin && customerId) {
        try {
          const vehicleIds = normalizeVehicles(vehicles);
          if (vehicleIds.length > 0) {
            const list = await fetchByVehiclesForAdmin(vehicleIds);
            setRedemptionHistory(list);
          } else {
            const list = await fetchByCustomerSubscriptions(customerId);
            setRedemptionHistory(list);
          }
        } catch (error) {
          console.error("Error fetching redemption history by customer:", error);
          setRedemptionHistory([]);
        } finally {
          setFetchLoading(false);
        }
        return;
      }

      if (!vehicles) {
        setRedemptionHistory([]);
        setFetchLoading(false);
        return;
      }

      const vehicleIds = normalizeVehicles(vehicles);
      if (vehicleIds.length === 0) {
        setRedemptionHistory([]);
        setFetchLoading(false);
        return;
      }

      try {
        const list = await fetchByVehicles(vehicleIds);
        setRedemptionHistory(list);
      } catch (error) {
        console.error("Error fetching redemption history:", error);
        setRedemptionHistory([]);
      } finally {
        setFetchLoading(false);
      }
    };

    fetchRedemptionHistory();
  }, [vehicles, customerId]);

  const handleClick = (redemption) => () => {
    if (redemption && redemption._id) {
      navigate(`/redemption/${redemption._id}`);
    }
  };

  const handleVehicleClick = (vehicle, event) => {
    event.stopPropagation();
    if (!vehicle) return;
    navigate(`/vehicle/${typeof vehicle === "object" ? vehicle._id : vehicle}`);
  };

  const handleRemoveRedemption = async (redemptionId, event) => {
    event.stopPropagation(); // Prevent row click navigation

    if (
      !window.confirm(
        "Are you sure you want to remove this redemption? This will void the Xero invoice and cannot be undone."
      )
    ) {
      return;
    }

    try {
      setRemoving(true);
      const response = await api(`v1/redemption/${redemptionId}`, "DELETE");

      if (response.data.success) {
        showSuccessToast("Redemption removed successfully");
        if (isAdmin && customerId) {
          const vehicleIds = normalizeVehicles(vehicles);
          if (vehicleIds.length > 0) {
            const list = await fetchByVehiclesForAdmin(vehicleIds);
            setRedemptionHistory(list);
          } else {
            const list = await fetchByCustomerSubscriptions(customerId);
            setRedemptionHistory(list);
          }
        } else {
          const vehicleIds = normalizeVehicles(vehicles);
          if (vehicleIds.length > 0) {
            const list = await fetchByVehicles(vehicleIds);
            setRedemptionHistory(list);
          }
        }
      } else {
        showErrorToast(response.data.message || "Failed to remove redemption");
      }
    } catch (error) {
      console.error("Error removing redemption:", error);
      showErrorToast("Failed to remove redemption. Please try again.");
    } finally {
      setRemoving(false);
    }
  };
  return (
    <div className="flex flex-column">
      <h3>Redemption History</h3>

      {/* Redemption Counts */}
      {redemptionHistory && redemptionHistory.length > 0 && (
        <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: "24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontWeight: "bold", color: "#2e7d32" }}>Valid Redemptions:</span>
                <span
                  style={{
                    backgroundColor: "#e8f5e8",
                    color: "#2e7d32",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "bold"
                  }}
                >
                  {redemptionHistory.filter((redemption) => redemption.active).length}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontWeight: "bold", color: "#d32f2f" }}>Removed Redemptions:</span>
                <span
                  style={{
                    backgroundColor: "#ffebee",
                    color: "#d32f2f",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "bold"
                  }}
                >
                  {redemptionHistory.filter((redemption) => !redemption.active).length}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontWeight: "bold", color: "#1976d2" }}>Total:</span>
              <span
                style={{
                  backgroundColor: "#e3f2fd",
                  color: "#1976d2",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontWeight: "bold"
                }}
              >
                {redemptionHistory.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {fetchLoading ? (
        <GlobalLoadingSpinner />
      ) : redemptionHistory && redemptionHistory.length > 0 ? (
        <div
          style={{
            width: "fit-content",
            maxWidth: "calc(100vw - 200px)",
            margin: "0 auto",
            overflowX: "auto"
          }}
        >
          <table className="table-auto border-collapse border border-gray-400">
            <thead>
              <tr>
                <th className="border border-gray-300 p-2">Redemption Date</th>
                <th className="border border-gray-300 p-2">Product</th>
                <th className="border border-gray-300 p-2">Vehicle</th>
                <th className="border border-gray-300 p-2">Merchant Store</th>
                <th className="border border-gray-300 p-2">Redeemed By</th>
                {sessionStorage.getItem("selectedPortal") === "Administrator" && (
                  <th className="border border-gray-300 p-2">Created By</th>
                )}
                <th className="border border-gray-300 p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {redemptionHistory.map((redemption) => (
                <tr
                  key={redemption._id}
                  onClick={handleClick(redemption)}
                  className={`cursor-pointer ${!redemption.active ? "bg-red-100 border-red-300" : ""}`}
                >
                  <td className={`border border-gray-300 p-2 ${!redemption.active ? "bg-red-50" : ""}`}>
                    {new Date(redemption.redemptionDateUTC).toLocaleDateString()}
                    {!redemption.active && <span className="ml-2 text-red-600 font-semibold">(REMOVED)</span>}
                  </td>
                  <td className={`border border-gray-300 p-2 ${!redemption.active ? "bg-red-50" : ""}`}>
                    {redemption.product.name}
                  </td>
                  <td
                    className={`border border-gray-300 p-2 ${!redemption.active ? "bg-red-50" : ""}`}
                    onClick={(e) => handleVehicleClick(redemption.vehicle, e)}
                  >
                    {redemption.vehicle.rego}
                  </td>
                  <td className={`border border-gray-300 p-2 ${!redemption.active ? "bg-red-50" : ""}`}>
                    {redemption.merchantStore.name}
                  </td>
                  <td className={`border border-gray-300 p-2 ${!redemption.active ? "bg-red-50" : ""}`}>
                    {redemption.merchantUser
                      ? `${redemption.merchantUser.firstName} ${redemption.merchantUser.lastName}`
                      : `N/A`}
                  </td>
                  {sessionStorage.getItem("selectedPortal") === "Administrator" && (
                    <td className={`border border-gray-300 p-2 ${!redemption.active ? "bg-red-50" : ""}`}>
                      {redemption.createdVia === "admin" || redemption.createdBy || redemption.createdByName
                        ? `Admin: ${redemption.createdByName || "Admin"}`
                        : redemption.createdVia === "merchant" || redemption.merchantUser
                        ? (() => {
                            const name = `${redemption.merchantUser?.firstName || ""} ${redemption.merchantUser?.lastName || ""}`.trim();
                            return name ? `Merchant: ${name}` : "Merchant";
                          })()
                        : "-"}
                    </td>
                  )}
                  <td className={`border border-gray-300 p-2 ${!redemption.active ? "bg-red-50" : ""}`}>
                    {redemption.active ? (
                      <button
                        onClick={(e) => handleRemoveRedemption(redemption._id, e)}
                        disabled={removing}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {removing ? "Removing..." : "Remove"}
                      </button>
                    ) : (
                      <span className="text-red-600 font-semibold">REMOVED</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div>No redemptions found</div>
      )}
    </div>
  );
};

export default RedemptionHistory;
